import { Link } from "react-router-dom";
import type { ComponentType, SVGProps } from "react";
import {
  ArrowRightIcon,
  CheckIcon,
  XIcon,
} from "./Icons";
import { GridPattern } from "./Patterns";
import { SITE } from "../config";
import { usePageMeta } from "../seo";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export type SolutionData = {
  path: string; // its entry in src/pages.json
  eyebrow: string;
  headline: string;
  lede: string;
  stats: { value: string; label: string; sub?: string }[];
  painFix: { pain: string; fix: string }[];
  highlights: {
    Icon: IconType;
    title: string;
    body: string;
  }[];
  relatedSolutions: { to: string; label: string }[];
};

export default function SolutionLayout({
  data,
}: {
  data: SolutionData;
}) {
  const {
    path,
    eyebrow,
    headline,
    lede,
    stats,
    painFix,
    highlights,
    relatedSolutions,
  } = data;
  usePageMeta(path);

  return (
    <>
      {/* ── Dark hero ────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            {eyebrow}
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            {headline}
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            {lede}
          </p>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────── */}
      <section className="border-y border-ink-200 bg-gradient-to-br from-white via-brand-50/40 to-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-3">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className="animate-fade-up border-l-2 border-brand-500 pl-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="bg-gradient-to-br from-brand-600 to-violet-600 bg-clip-text text-4xl font-semibold tracking-tight text-transparent">
                  {s.value}
                </p>
                <p className="mt-2 text-sm font-medium text-ink-900">
                  {s.label}
                </p>
                {s.sub && (
                  <p className="mt-0.5 text-xs text-ink-500">{s.sub}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pain / Fix ───────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            Where it hurts. What helps.
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            Three common problems, and the features that help.
          </h2>
        </div>
        <div className="space-y-5">
          {painFix.map((item, i) => (
            <div
              key={item.pain}
              className="animate-fade-up grid gap-5 md:grid-cols-2"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="rounded-xl border border-rose-100 bg-gradient-to-br from-rose-50 to-white p-6 shadow-sm">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-rose-100/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                  <XIcon width={10} height={10} strokeWidth={2.6} />
                  The pain
                </div>
                <p className="text-sm leading-relaxed text-ink-700">
                  {item.pain}
                </p>
              </div>
              <div className="rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50/80 to-violet-50/60 p-6 shadow-sm">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-brand-100/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                  <CheckIcon width={10} height={10} strokeWidth={2.6} />
                  The fix
                </div>
                <p className="text-sm leading-relaxed text-ink-700">
                  {item.fix}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Highlights ───────────────────────────────────────── */}
      <section className="border-y border-ink-200 bg-gradient-to-br from-brand-50/60 via-white to-violet-50/50">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              What you'll use
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              The features that matter most here.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {highlights.map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className="animate-fade-up rounded-xl border border-ink-200 bg-white p-6 shadow-sm"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/20">
                  <Icon width={20} height={20} />
                </div>
                <h3 className="mb-2 text-base font-semibold text-ink-900">
                  {title}
                </h3>
                <p className="text-sm leading-relaxed text-ink-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Related solutions + CTA ──────────────────────────── */}
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute top-0 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-300">
                Also see
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Other ways teams run ZopDesk.
              </h2>
              <div className="mt-6 flex flex-wrap gap-2">
                {relatedSolutions.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-ink-200 backdrop-blur transition hover:border-brand-300/40 hover:bg-white/10 hover:text-white"
                  >
                    {r.label}
                    <ArrowRightIcon width={12} height={12} />
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur">
              <p className="text-sm font-semibold text-white">
                Try it with your own inbox.
              </p>
              <p className="text-xs text-ink-400">
                Free during the beta. Sign in with Google.
              </p>
              <a
                href={SITE.appUrl}
                className="group mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400"
              >
                Start free
                <ArrowRightIcon
                  width={14}
                  height={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
              <Link
                to="/contact"
                className="text-center text-xs font-semibold text-ink-300 hover:text-white"
              >
                Or talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
